import {
  FaInfoCircle,
  FaFileInvoice,
  FaUsers,
  FaServer,
  FaSitemap,
  FaChartBar,
  FaClipboardList,
  FaWarehouse,
  FaStore,
  FaUserTie,
  FaIndustry,
  FaCog,
  FaIdBadge,
} from "react-icons/fa";
import { getFirmTypeStyle } from "./firmTypeStyles";

export const BASE_TABS = [
  { id: "info", icon: FaInfoCircle, label: "Bilgi", bg: "bg-indigo-500", permission: "firms.info" },
  { id: "devices", icon: FaServer, label: "Cihazlar", bg: "bg-emerald-500", permission: "firms.devices" },
  { id: "stats", icon: FaChartBar, label: "Performans", bg: "bg-teal-500", permission: "firms.stats" },
  { id: "bills", icon: FaFileInvoice, label: "Faturalar", bg: "bg-amber-500", permission: "firms.bills" },
  { id: "productSettings", icon: FaCog, label: "Ürün Ayarları", bg: "bg-purple-500", permission: "firms.productSettings" },
  { id: "subfirms", icon: FaSitemap, label: "Alt Firmalar", bg: "bg-violet-500", permission: "firms.belowFirms" },
  { id: "users", icon: FaUsers, label: "Kullanıcılar", bg: "bg-blue-500", permission: "firms.users" },
  { id: "reports", icon: FaClipboardList, label: "Raporlar", bg: "bg-red-500", permission: ["firms.logConsumption", "firms.logInfo", "firms.logBill", "firms.logSettings", "firms.logOperation", "firms.logQuota"] },
];

// Bayi panelinde firma sekmeleri, depo `firms.*` modelinden bağımsız olarak
// firma tipine göre `dealers.*` / `customers.*` yetkilerine bağlanır. Genel
// (rol/çalışan şablonu) yetkileri `firms.*` altında kalır.
const dealerNamespaceForFirm = (firmType) =>
  firmType === "customer" ? "customers" : "dealers";

const remapPermForDealer = (permission, ns) => {
  const mapOne = (p) => {
    if (typeof p !== "string" || !p.startsWith("firms.")) return p;
    const suffix = p.slice("firms.".length);
    // Genel kalan yetkiler (tip bağımsız) firms.* altında bırakılır
    if (suffix.startsWith("roleTemplates") || suffix.startsWith("employeeTemplates")) return p;
    return `${ns}.${suffix}`;
  };
  return Array.isArray(permission) ? permission.map(mapOne) : mapOne(permission);
};

/**
 * Sekme içindeki ek izin kontrolleri için: bayi rolünde `firms.*` izni firma
 * tipine göre `dealers.*` / `customers.*` olur (şablon izinleri hariç) —
 * sekme listesiyle aynı çeviri.
 */
export const firmTabPermission = (firm, permission, { role } = {}) =>
  role === "dealer"
    ? remapPermForDealer(permission, dealerNamespaceForFirm(firm?.firmType))
    : permission;

/**
 * Kaldırılan sekme id'leri (#298: şablonlar listeyle aynı sekmede) — eski
 * `toPageTab` / kayıtlı durum gelirse yeni sekmeye düşsün.
 */
const LEGACY_TAB_IDS = { roleTemplates: "users", employeeTemplates: "employees" };
export const normalizeFirmTabId = (id) => LEGACY_TAB_IDS[id] || id;

export const getTabsForFirm = (firm, { role } = {}) => {
  const isDealer = firm?.firmType === "dealer";
  const isCustomer = firm?.firmType === "customer";
  const isDealerRoleUser = role === "dealer";
  const ns = dealerNamespaceForFirm(firm?.firmType);
  const perm = (p) => (isDealerRoleUser ? remapPermForDealer(p, ns) : p);

  return BASE_TABS
    .flatMap((tab) => {
      // Bayide Alt Bayiler + Müşteriler TEK "Müşteriler" sekmesi (#376);
      // iki eski iznin herhangi biriyle görünür, satırlar kendi iznine bağlı
      if (tab.id === "subfirms" && isDealer)
        return [
          {
            ...tab,
            icon: FaUserTie,
            label: "Müşteriler",
            permission: [perm(tab.permission), isDealerRoleUser ? "dealers.customers" : "firms.customers"],
          },
        ];
      if (tab.id === "users") {
        // Kullanıcılar + kullanıcı şablonları TEK sekme (#298): liste ya da
        // şablon izninden biri yeter; sekme içinde her parça kendi iznine bağlı.
        const usersTab = { ...tab, permission: [perm(tab.permission), "firms.roleTemplatesRead"] };
        // Çalışan / çalışan şablonu yönetimi yalnız merkezde; bayi panelinde yok (#362)
        if (isCustomer && !isDealerRoleUser) {
          return [
            usersTab,
            { id: "employees", icon: FaIdBadge, label: "Çalışanlar", bg: "bg-orange-500", permission: ["firms.employees", "firms.employeeTemplatesRead"] },
          ];
        }
        return [usersTab];
      }
      return [{ ...tab, permission: perm(tab.permission) }];
    });
};

// `size` boyut sınıfı — varsayılan detay başlığındaki ikon; seçici satırları
// (FirmDropdown) daha küçüğünü geçer. Renk tek kaynaktan (utils/firmTypeStyles, #215).
export const getFirmIcon = (firm, size = "w-5 h-5") => {
  const { icon } = getFirmTypeStyle(firm);
  if (firm?.firmType === "storage") return <FaWarehouse className={`${size} ${icon}`} />;
  if (firm?.firmType === "dealer") return <FaStore className={`${size} ${icon}`} />;
  // Kurumsal müşteri kendi ikonunu korur, rengi müşteri ailesinden
  if (firm?.isCorporate) return <FaIndustry className={`${size} ${icon}`} />;
  return <FaUserTie className={`${size} ${icon}`} />;
};

/** { label, color } — color: rozet sınıfları (bg + text + border) */
export const getFirmTypeBadge = (firm) => {
  const style = getFirmTypeStyle(firm);
  return { label: style.label, color: style.badge };
};

/** Firma detay sayfasındaki tüm olası tab permission key'leri */
/**
 * Bu firmanın detayına girilebilir mi?
 *
 * Detay sayfası sekmeleri getTabsForFirm + izin filtresiyle üretiliyor; hiç
 * sekme kalmıyorsa sayfa 403 gösteriyor. Liste de AYNI hesabı yapmalı, aksi
 * halde kart tıklanabilir görünüp kullanıcıyı 403'e götürüyor.
 *
 * Firma bazlı olması şart: bayide sekme yetkileri firma tipine göre
 * `dealers.*` / `customers.*` olarak ayrışıyor — tüm yetkilerin birleşimine
 * bakmak, yalnızca bayi yetkisi olan kullanıcıda müşteri kartını da
 * tıklanabilir yapıyordu.
 */
export const canOpenFirmDetail = (
  firm,
  { role, hasPermission, hasAnyPermission } = {},
) => {
  if (!firm) return false;
  return getTabsForFirm(firm, { role }).some((tab) => {
    if (!tab.permission) return true;
    if (Array.isArray(tab.permission)) return hasAnyPermission?.(...tab.permission);
    return hasPermission?.(tab.permission);
  });
};

export const ALL_FIRM_TAB_PERMISSIONS = [
  "firms.info",
  "firms.devices",
  "firms.stats",
  "firms.bills",
  "firms.productSettings",
  "firms.belowFirms",
  "firms.users",
  "firms.logConsumption",
  "firms.logInfo",
  "firms.logBill",
  "firms.logSettings",
  "firms.logOperation",
  "firms.logQuota",
  "firms.roleTemplatesRead",
  "firms.customers",
  "firms.employees",
  "firms.employeeTemplatesRead",
];

/** Bayi panelinde bir firmaya girişi mümkün kılan tüm tab yetkileri (yeni taksonomi) */
export const ALL_DEALER_FIRM_TAB_PERMISSIONS = [
  // Bayiler
  "dealers.info", "dealers.devices", "dealers.stats", "dealers.bills",
  "dealers.productSettings", "dealers.belowFirms", "dealers.users", "dealers.customers",
  "dealers.logConsumption", "dealers.logInfo", "dealers.logBill", "dealers.logSettings", "dealers.logOperation", "dealers.logQuota",
  // Müşteriler
  "customers.info", "customers.devices", "customers.stats", "customers.bills",
  "customers.productSettings", "customers.users", "customers.employees",
  "customers.logConsumption", "customers.logInfo", "customers.logBill", "customers.logSettings", "customers.logOperation", "customers.logQuota",
  // Genel
  "firms.roleTemplatesRead",
];

export const DEALER_ROLES = ["dealer"];

export const isDealerRole = (role) => role === "dealer";
